import { useAppNavigate } from "hooks/useAppNavigate";
import React from "react";
import { recaptchaSiteKey } from "env";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import RecaptchaField, { RecaptchaFieldRef } from "components/RecaptchaField";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import { startPasswordlessLogin, isRecaptchaRequiredError, isServiceUnavailableError } from "services/auth.service";
import { useLoginPage } from "hooks/useLoginPage";
import { isValidEmail } from "utils/validators";
import TextInput from "components/TextInput";
import { useText } from "context/TextProvider";
import { setFlowState } from "../../storage";
import { useClearStorageOnQueryParam } from "hooks/useClearStorageOnQueryParam";
import { trackEvent } from "services/analytics.service";
import { AnalyticsEvent, AnalyticsFlow, AnalyticsMethod, AnalyticsStage, AnalyticsStatus } from "services/analyticsEvents";
import { useScreenTimeTracking } from "hooks/useScreenTimeTracking";

const DeviceGrantLogin = (): JSX.Element => {
  const text = useText();
  const appNavigate = useAppNavigate();
  useClearStorageOnQueryParam();
  useScreenTimeTracking(AnalyticsEvent.TIME_ON_DEVICE_LOGIN_SCREEN);

  const [formData, setFormData] = React.useState({
    email: "",
  });
  const [recaptchaToken, setRecaptchaToken] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const recaptchaRef = React.useRef<RecaptchaFieldRef>(null);
  const { configId, loginPageConfig } = useLoginPage();
  const enableRecaptcha = loginPageConfig?.enableRecaptcha ?? false;

  const validateFormData = ({ email }: { email: string }): boolean => {
    if (!email) {
      setErrorMessage(text.deviceGrant.login.emailRequired);
      return false;
    }
    if (!isValidEmail(email)) {
      setErrorMessage(text.deviceGrant.login.invalidEmail);
      return false;
    }
    if (enableRecaptcha && !recaptchaToken) {
      setErrorMessage(text.deviceGrant.login.recaptchaRequired);
      return false;
    }
    setErrorMessage("");
    return true;
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleLogin = async (data: { email: string }) => {
    if (!validateFormData(data)) return;
    setLoading(true);
    setErrorMessage("");
    trackEvent(AnalyticsEvent.DEVICE_PASSWORDLESS_LOGIN_STARTED, { method: AnalyticsMethod.PASSWORDLESS, flow: AnalyticsFlow.DEVICE_GRANT, status: AnalyticsStatus.STARTED });
    setFlowState(configId, { loginStartedAt: Date.now() });
    try {
      const codes = await startPasswordlessLogin({
        loginId: data.email,
        recaptchaToken,
        enableRecaptcha,
        url: loginPageConfig?.urls?.start_passwordless_login,
      });
      setFlowState(configId, { route: "otp-verification", email: data.email, code: codes.code });
      appNavigate(`/device-grant-flow/otpVerification`);
    } catch (err) {
      console.error("Login error:", err);
      setErrorMessage(
        isRecaptchaRequiredError(err)
          ? text.deviceGrant.login.recaptchaRequired
          : isServiceUnavailableError(err)
          ? text.common.serviceUnavailable
          : text.deviceGrant.login.genericError
      );
      trackEvent(AnalyticsEvent.DEVICE_PASSWORDLESS_LOGIN_FAILED, {
        method: AnalyticsMethod.PASSWORDLESS,
        flow: AnalyticsFlow.DEVICE_GRANT,
        status: AnalyticsStatus.FAILED,
        stage: AnalyticsStage.START,
      });
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
      setRecaptchaToken("");
    }
  };

  return (
    <>
      <PageMeta name="Device Grant Login" />
      <PageTitle>{text.deviceGrant.login.title}</PageTitle>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <TextInput
        type="email"
        value={formData.email}
        placeholder={text.deviceGrant.login.emailPlaceholder}
        onChange={(e) => onChange(e, "email")}
        disabled={loading}
      />

      {enableRecaptcha && (
        <RecaptchaField
          ref={recaptchaRef}
          sitekey={recaptchaSiteKey}
          onChange={(token) => setRecaptchaToken(token ?? "")}
          onError={(message) => setErrorMessage(message)}
          theme="dark"
          size="normal"
          tabIndex={0}
        />
      )}

      <LoadingButton
        loading={loading}
        onClick={() => handleLogin(formData)}
        disabled={loading}
        sx={{
          width: "100%",
          height: "3.5rem",
          mb: "1rem",
        }}
      >
        {text.deviceGrant.login.submitButton}
      </LoadingButton>
    </>
  );
};

export default React.memo(DeviceGrantLogin);
